import React, { ReactNode } from 'react'
import styles from './matchCard.module.scss'
import { MatchUpUI, SidesUI, SocialMatchUpUI } from 'types/global'
import { createDisplayDate, isNoScoreMatch } from 'utils'
import classNames from 'classnames'
import SidePlayers from '../generic/SidePlayers'
import { MatchUpStatusEnum, SideEnum } from 'types/api/enums'
import WinnerOrLoserTag from '../generic/WinnerOrLoserTag'
import { ReactComponent as WinCheckMark } from 'images/icons/icn_win_checkmark.svg'

export const getSidesKeysRelativeToPlayer = (playerId: string, sides: SidesUI) =>
  sides.SIDE1.find(p => p.id === playerId)
    ? { s1Key: SideEnum.SIDE1, s2Key: SideEnum.SIDE2 }
    : { s1Key: SideEnum.SIDE2, s2Key: SideEnum.SIDE1 }

const NoScore = () => <span className={styles.score}>-</span>

export interface MatchCardProps {
  match: MatchUpUI | SocialMatchUpUI
  profileViewedId: string
  iconType?: 'flag' | 'avatar'
  truncateName?: boolean
  className?: string
  headerRight?: ReactNode
  footer?: ReactNode
}

const MatchCard = ({
  match,
  profileViewedId,
  headerRight,
  footer,
  truncateName = false,
  className = '',
  iconType = 'flag',
}: MatchCardProps) => {
  const { start, sides, sets, status, winningSide, type } = match
  const isMatchSuspended = status === MatchUpStatusEnum.SUSPENDED
  const hasMatchScore = !isNoScoreMatch(status)
  const { s1Key, s2Key } = getSidesKeysRelativeToPlayer(profileViewedId, sides)
  const hasSide1Won = s1Key === winningSide && !isMatchSuspended
  const hasSide2Won = s2Key === winningSide && !isMatchSuspended

  return (
    <article className={classNames(styles.matchCard, className)}>
      <header className={styles.matchCardHeader}>
        <time dateTime={start}>
          {createDisplayDate(new Date(start), { withDay: true, withYear: true })}
        </time>
        {headerRight && <div className={styles.headerRight}>{headerRight}</div>}
      </header>
      <div
        className={classNames(styles.matchCardBody, {
          [styles.doublesMatch]: sides[s1Key].length > 1,
        })}
      >
        <div className={styles.matchCardMain}>
          <section
            className={classNames(styles.matchSides, {
              [styles.doublesMatch]: sides[s1Key].length > 1,
            })}
          >
            <SidePlayers
              sideNumber={1}
              side={sides[s1Key]}
              iconType={iconType}
              isWinner={hasSide1Won}
              truncateName={truncateName}
              matchType={type}
              showRating
              showConfidence
              withLink
            />
            <SidePlayers
              sideNumber={2}
              side={sides[s2Key]}
              iconType={iconType}
              isWinner={hasSide2Won}
              truncateName={truncateName}
              matchType={type}
              showRating
              showConfidence
              withLink
            />
          </section>
          <section
            className={classNames(styles.matchStatus, {
              [styles.doublesMatch]: sides[s1Key].length > 1,
            })}
          >
            <p className={styles.sideStatus}>
              {hasSide1Won && <WinCheckMark className={styles.winCheckMark} />}
              <WinnerOrLoserTag status={status} isWinTag={hasSide1Won} />
              {!hasMatchScore && !hasSide1Won && <NoScore />}
            </p>
            <p className={styles.sideStatus}>
              {hasSide2Won && <WinCheckMark className={styles.winCheckMark} />}
              <WinnerOrLoserTag status={status} isWinTag={hasSide2Won} />
              {!hasMatchScore && !hasSide2Won && <NoScore />}
            </p>
          </section>
          {hasMatchScore && (
            <section
              className={classNames(styles.matchScores, {
                [styles.doublesMatch]: sides[s1Key].length > 1,
                [styles.suspended]: isMatchSuspended,
              })}
            >
              {sets.map((set, i) => {
                const tb = set.tiebreaker
                const score1 = set.type === 'set' ? set.points[s1Key] : `[${set.points[s1Key]}]`
                const score2 = set.type === 'set' ? set.points[s2Key] : `[${set.points[s2Key]}]`

                return (
                  <div key={i} className={styles.matchScoreSet}>
                    <p
                      className={classNames(styles.sideScore, {
                        [styles.isWinner]: set.points[s1Key] > set.points[s2Key],
                      })}
                    >
                      {score1}
                      {tb && <sup>{tb.points[s1Key]}</sup>}
                    </p>
                    <p
                      className={classNames(styles.sideScore, {
                        [styles.isWinner]: set.points[s2Key] > set.points[s1Key],
                      })}
                    >
                      {score2}
                      {tb && <sup>{tb.points[s2Key]}</sup>}
                    </p>
                  </div>
                )
              })}
            </section>
          )}
        </div>
        {footer}
      </div>
    </article>
  )
}

export default MatchCard
